import { addDays, differenceInDays, format } from 'date-fns';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useBooking } from '../contexts/BookingContext';
import { useClosedDays } from '../hooks/useClosedDays';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/date-selection-light.css';
import DoubleDateRangePicker from './DoubleDateRangePicker';

const DateSelection = ({ onDateSelect, initialDates }) => {
  const today = new Date();
  const defaultEndDate = addDays(today, 2);

  // Establecer horarios fijos: 9:00 AM para inicio, 21:00 PM para fin
  const [startDate, setStartDate] = useState(() => {
    if (initialDates?.start) {
      return format(new Date(initialDates.start), 'yyyy-MM-dd');
    }
    return format(today, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => {
    if (initialDates?.end) {
      return format(new Date(initialDates.end), 'yyyy-MM-dd');
    }
    return format(defaultEndDate, 'yyyy-MM-dd');
  });
  const [error, setError] = useState('');

  const { t } = useTranslation();
  const { clearAvailabilityCache } = useBooking();
  const { closedDays } = useClosedDays();

  // Sincronizar el estado local con las props cuando cambien
  useEffect(() => {
    if (initialDates?.start && initialDates?.end) {
      setStartDate(format(new Date(initialDates.start), 'yyyy-MM-dd'));
      setEndDate(format(new Date(initialDates.end), 'yyyy-MM-dd'));
    }
    // No resetear a fechas por defecto si no hay initialDates
    // Esto permite al usuario seleccionar fechas libremente
  }, [initialDates]);

  const calculateDuration = () => {
    if (!startDate || !endDate) return { days: 0 };
    
    const startDateTime = new Date(startDate + 'T09:00:00');
    const endDateTime = new Date(endDate + 'T21:00:00');
    const days = differenceInDays(endDateTime, startDateTime) + 1; // +1 para incluir el día de inicio
    
    return { days };
  };

  const handleSubmit = () => {
    setError('');

    if (!startDate || !endDate) {
      setError(t('date.error.required'));
      return;
    }

    // Validar que la fecha de inicio no sea anterior a hoy
    const today = new Date();
    const selectedStartDate = new Date(startDate);
    
    // Comparar solo las fechas (sin horas)
    const todayDate = format(today, 'yyyy-MM-dd');
    const selectedStartDateStr = format(selectedStartDate, 'yyyy-MM-dd');
    
    if (selectedStartDateStr < todayDate) {
      setError(t('date.error.start.past'));
      return;
    }

    const { days } = calculateDuration();
    if (days < 1) {
      setError(t('date.error.min.days'));
      return;
    }

    if (closedDays.has(startDate) || closedDays.has(endDate)) {
      setError(t('date.error.closed'));
      return;
    }


    if (startDate && endDate) {
      // Limpiar el caché de disponibilidad cuando se seleccionan nuevas fechas
      clearAvailabilityCache();
      
      // Crear fechas con horarios fijos
      const startDateTime = new Date(startDate + 'T09:00:00');
      const endDateTime = new Date(endDate + 'T21:00:00');
      
      onDateSelect({
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        totalDays: days
      });
    }
  };

  const handleRangeChange = (newStartDate, newEndDate) => {
    // DateRangePicker entrega strings "yyyy-MM-dd"
    setStartDate(newStartDate || '');
    setEndDate(newEndDate || '');
  };

  const { days } = calculateDuration();

  return (
    <div className="date-selection-light">
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold font-orbitron mb-2">{t('date.title')}</h2>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div className="flex gap-2 items-center text-red-400">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Información de horarios - Solo en desktop */}
        <div className="hidden md:block mb-8">
          <div className="bg-background border border-primary rounded-xl p-6">
            <div className="flex gap-2 items-center mb-4">
              <Clock size={20} className="text-primary" />
              <h3 className="font-semibold">{t('date.schedule.info.title')}</h3>
            </div>
            <ul className="text-sm text-gray-300 space-y-2">
              <li>{t('date.schedule.info.pickup')}</li>
              <li>{t('date.schedule.info.return')}</li>
              <li>{t('date.schedule.info.minimum')}</li>
              <li>{t('date.schedule.info.maximum')}</li>
              <li>{t('date.schedule.info.advance')}</li>
            </ul>
          </div>
        </div>

        {/* Información de horarios - Solo en mobile */}
        <div className="md:hidden mb-6">
          <div className="bg-background border border-primary rounded-xl p-4">
            <div className="flex gap-2 items-center mb-3">
              <Clock size={18} className="text-primary" />
              <h3 className="font-semibold text-sm">{t('date.schedule.title')}</h3>
            </div>
            <div className="text-xs text-gray-300 space-y-1">
              <div>{t('date.schedule.mobile.times')}</div>
              <div>{t('date.schedule.mobile.duration')}</div>
            </div>
          </div>
        </div>

        <div className="mb-8 space-y-2">
          <label className="block text-sm font-medium flex items-center gap-2">
            <Calendar size={16} className="text-primary flex-shrink-0" />
            <span>{t('date.pickup.label')} / {t('date.return.label')}</span>
          </label>
          <DoubleDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={handleRangeChange}
            minDate={today}
            closedDates={closedDays}
          />
          <div className="grid md:grid-cols-2 gap-2">
            <small className="text-xs">{t('date.pickup.help')}</small>
            <small className="text-xs md:text-right">{t('date.return.help')}</small>
          </div>
        </div>

        {startDate && endDate && (
          <div className="bg-background border border-primary rounded-xl p-6 mb-8">
            <div className="flex gap-2 items-center">
              <Clock size={20} className="text-primary" />
              <span className="font-medium">{t('date.duration.label')}</span>
              <span className="text-lg font-semibold text-primary">
                {days} {days > 1 ? t('date.days') : t('date.day')}
              </span>
            </div>
            <div className="mt-2 text-sm text-gray-300">
              {t('date.duration.range', {
                start: format(new Date(startDate), 'dd/MM/yyyy'),
                end: format(new Date(endDate), 'dd/MM/yyyy')
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            className="px-8 py-4 bg-primary text-primary-dark font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 flex items-center gap-2"
            onClick={handleSubmit}
          >
            {t('date.continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateSelection; 